import { useEffect, useMemo, useState } from "react";
import { api, extractError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Alert, AlertDescription } from "./ui/alert";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { Check, Split, X, Loader2, Edit3 } from "lucide-react";

const CHIP_META = {
  warranty:  { label: "Warranty",  classes: "border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100" },
  company:   { label: "Company",   classes: "border-slate-300 bg-slate-100 text-slate-700 hover:bg-slate-200" },
  driver:    { label: "Driver",    classes: "border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100" },
  lessee:    { label: "Lessee",    classes: "border-teal-200 bg-teal-50 text-teal-700 hover:bg-teal-100" },
  // Phase 4a — set from the RepairHistory drawer only, never a primary
  // chip on the correction row. Amber-orange tone signals "shop-credit
  // pending" (future Phase 4b Shop Credits page will surface these).
  duplicate: { label: "Duplicate", classes: "border-orange-200 bg-orange-50 text-orange-700 hover:bg-orange-100" },
  // Phase 1 Reject — line-level reject picker. Rose tone matches the
  // invoice-level REJECTED pill. Opens a small overlay to collect
  // reason + comment before applying.
  rejected:  { label: "Rejected",  classes: "border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100" },
};

// Pass-through buckets — no expense/deduction, reverse-charge just clears
// the allocation. Kept in one place so the reverse dialog and status
// pills stay in sync with the backend `_PASS_THROUGH_BUCKETS`.
const PASS_THROUGH_BUCKETS = new Set(["warranty", "duplicate", "rejected"]);

const formatMoney = (v) => {
  const n = Number(v);
  return isFinite(n) ? n.toLocaleString(undefined, { style: "currency", currency: "USD" }) : "—";
};

const closeTo = (a, b, eps = 0.01) => Math.abs(Number(a) - Number(b)) <= eps;

// ---------------------------------------------------------------------------
// LesseeCombobox — free-text lessee picker with historical autocomplete.
// Emits "add new" for a query with no exact match, so operators can enter
// a brand-new lessee inline (Phase 2a — no structured Alvys source).
// ---------------------------------------------------------------------------
export function LesseeCombobox({ companyId, value, onChange, testid, autoFocus = false }) {
  const [q, setQ] = useState(value || "");
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { setQ(value || ""); }, [value]);

  useEffect(() => {
    if (!companyId) { setResults([]); return; }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(() => {
      const params = { company_id: companyId, limit: 8 };
      if (q && q.trim().length >= 1) params.q = q.trim();
      api.get("/equipment-repairs/lessees/search", { params })
        .then((r) => { if (!cancelled) setResults(r.data || []); })
        .catch(() => setResults([]))
        .finally(() => { if (!cancelled) setLoading(false); });
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q, companyId]);

  const exact = q.trim() && results.some(
    (r) => (r.name || "").trim().toLowerCase() === q.trim().toLowerCase(),
  );
  const showAddNew = open && q.trim().length > 0 && !exact;

  return (
    <div className="relative">
      <Input
        autoFocus={autoFocus}
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); onChange?.(e.target.value, "manual"); }}
        onFocus={() => setOpen(true)}
        placeholder="Type or pick a lessee…"
        className="h-9 text-sm"
        data-testid={testid}
      />
      {open && (results.length > 0 || showAddNew) && (
        <div className="absolute z-30 mt-1 max-h-56 w-full overflow-auto rounded-md border border-slate-200 bg-white shadow">
          {results.map((r) => (
            <button
              key={r.name}
              type="button"
              className="flex w-full items-baseline justify-between gap-2 px-3 py-1.5 text-left text-sm hover:bg-slate-50"
              onClick={() => { setQ(r.name); onChange?.(r.name, "historical_autocomplete"); setOpen(false); }}
              data-testid={`${testid}-option-${r.name.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}`}
            >
              <span className="truncate text-slate-800">{r.name}</span>
              <span className="shrink-0 text-[11px] text-slate-500">{r.occurrence_count} use{r.occurrence_count === 1 ? "" : "s"}</span>
            </button>
          ))}
          {showAddNew && (
            <button
              type="button"
              className="flex w-full items-center gap-2 border-t border-slate-100 bg-teal-50/60 px-3 py-1.5 text-left text-sm hover:bg-teal-50"
              onClick={() => { onChange?.(q.trim(), "manual"); setOpen(false); }}
              data-testid={`${testid}-add-new`}
            >
              <span className="text-teal-700">+</span>
              <span className="text-slate-800">Add new lessee: <span className="font-medium">"{q.trim()}"</span></span>
            </button>
          )}
          {loading && results.length === 0 && !showAddNew && (
            <div className="px-3 py-2 text-xs text-slate-400">Loading…</div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// DriverPicker — small typeahead against /tms/drivers/search
// ---------------------------------------------------------------------------
export function DriverCombobox({ value, name, onChange, testid, autoFocus = false }) {
  const [q, setQ] = useState(name || "");
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  useEffect(() => {
    if (!q || q.length < 2) { setResults([]); return; }
    let cancelled = false;
    const t = setTimeout(() => {
      api.get("/tms/drivers/search", { params: { q, limit: 8 } })
        .then((r) => { if (!cancelled) setResults(r.data || []); })
        .catch(() => setResults([]));
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q]);
  return (
    <div className="relative">
      <Input
        autoFocus={autoFocus}
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder="Search driver…"
        className="h-8 text-sm"
        data-testid={testid}
      />
      {open && results.length > 0 && (
        <div className="absolute z-30 mt-1 max-h-48 w-full overflow-auto rounded-md border border-slate-200 bg-white shadow">
          {results.map((r) => (
            <button
              key={r.id}
              className="block w-full px-3 py-1.5 text-left text-sm hover:bg-slate-50"
              onClick={() => { setQ(r.label); onChange(r.id, r.label); setOpen(false); }}
            >{r.label}</button>
          ))}
        </div>
      )}
      {value && (
        <button
          className="absolute right-1 top-1 rounded p-1 text-slate-400 hover:bg-slate-100"
          onClick={() => { setQ(""); onChange(null, null); }}
        ><X className="h-3 w-3" /></button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// AllocationEditor — single row of a corrections table (write-mode)
// ---------------------------------------------------------------------------
export function AllocationEditor({ correction, invoice, onChange, readOnly = false }) {
  const { user } = useAuth();
  const canReverse = !readOnly && (user?.role === "admin" || user?.role === "accounting");
  const [busy, setBusy] = useState(false);
  const [driverDialogOpen, setDriverDialogOpen] = useState(false);
  const [lesseeDialogOpen, setLesseeDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [splitOpen, setSplitOpen] = useState(false);
  const [reverseOpen, setReverseOpen] = useState(false);
  const [reversing, setReversing] = useState(false);
  const [reverseError, setReverseError] = useState(null);

  // Phase 2a — hard swap: Driver hides for leased-out equipment, Lessee
  // takes its place. Warranty + Company are unchanged.
  // Phase 1 Reject — appended to every bucket set (leased-out too).
  const isLeasedOut = invoice?.equipment_status === "leased_out";
  const buckets = isLeasedOut
    ? ["warranty", "company", "lessee", "rejected"]
    : ["warranty", "company", "driver", "rejected"];

  const currentBucket = useMemo(() => {
    if (!correction.charge_allocations || correction.charge_allocations.length === 0) return null;
    if (correction.charge_allocations.length > 1) return "split";
    return correction.charge_allocations[0].charge_type;
  }, [correction.charge_allocations]);

  const isCharged = correction.status === "charged";

  const applyAllocations = async (allocations) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/equipment-repairs/corrections/${correction.id}/allocations`, { allocations });
      onChange?.(data);
      toast.success("Allocation saved");
      window.dispatchEvent(new Event("repairs:refresh-badge"));
    } catch (e) {
      toast.error(extractError(e));
    } finally { setBusy(false); }
  };

  // Reverse-charge — external-first (Alvys voids, then local expense voids +
  // clear allocations + drop status to unassigned). If Alvys void fails the
  // backend returns HTTP 424 with `{code:"alvys_void_failed"}` and does not
  // touch local state, so the modal stays open and the user can retry.
  //
  // Why 424 and not 502: Cloudflare's preview edge intercepts 5xx responses
  // from origin and returns its own error page, stripping our JSON body.
  // 424 Failed Dependency is standards-correct for upstream failures and
  // passes through Cloudflare so the frontend can read the error code.
  const reverseCharge = async () => {
    if (reversing) return;
    setReversing(true);
    setReverseError(null);
    try {
      const { data } = await api.post(`/equipment-repairs/corrections/${correction.id}/reverse-charge`);
      const parts = [];
      if (data.reversed_count) parts.push(`${data.reversed_count} charges`);
      if (data.alvys_deductions_voided?.length) parts.push(`${data.alvys_deductions_voided.length} Alvys voided`);
      toast.success(`Reversed. Correction is now Unassigned.${parts.length ? " · " + parts.join(" · ") : ""}`);
      setReverseOpen(false);
      onChange?.();
      window.dispatchEvent(new CustomEvent("repairs:invoice-reload", { detail: { invoiceId: invoice?.id } }));
      window.dispatchEvent(new Event("repairs:refresh-badge"));
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      const code = detail && typeof detail === "object" ? detail.code : null;
      if (status === 424 && code === "alvys_void_failed") {
        // Keep modal open, render inline red banner, allow retry.
        setReverseError(detail?.message || "Could not void Alvys deduction. Please retry.");
      } else {
        toast.error(extractError(e));
      }
    } finally { setReversing(false); }
  };

  const setSingle = async (bucket, driverId = null, driverName = null, overrideFrom = null) => {
    const alloc = { charge_type: bucket, amount: correction.amount };
    if (bucket === "driver") {
      alloc.assigned_driver_id = driverId;
      alloc.driver_name = driverName;
      // Only stamp override-audit fields when the user actually chose a
      // driver different from the invoice suggestion (or when there was
      // a suggestion at all but user picked another). Backend requires
      // both `_id` and `_name` populated together.
      if (overrideFrom && overrideFrom.id && overrideFrom.id !== driverId) {
        alloc.driver_overridden_from_suggested_id = overrideFrom.id;
        alloc.driver_overridden_from_suggested_name = overrideFrom.name || "";
      }
    }
    await applyAllocations([alloc]);
  };

  const setLesseeSingle = async ({ lessee_name, lessee_contact, lessee_source }) => {
    const alloc = {
      charge_type: "lessee",
      amount: correction.amount,
      lessee_name,
      lessee_contact: lessee_contact || null,
      lessee_source: lessee_source || "manual",
    };
    await applyAllocations([alloc]);
  };

  // Phase 1 Reject — line-level. Reason + comment are required by the
  // backend allocations validator; picker is opened via the Rejected chip.
  const setRejectSingle = async ({ reject_reason, reject_comment }) => {
    const alloc = {
      charge_type: "rejected",
      amount: correction.amount,
      reject_reason,
      reject_comment,
    };
    await applyAllocations([alloc]);
  };

  const setDriverBucket = () => {
    // Always open the picker (pre-seeded to suggestion when available) so
    // the user can accept-or-override in one step. Previously the chip
    // path auto-committed the suggestion with no way to change it — that
    // was the Phase 1 UX gap this fix addresses.
    setDriverDialogOpen(true);
  };

  const setLesseeBucket = () => {
    // Phase 2a — always open picker for lessee assignment. Pre-seeds to
    // the existing lessee_name if this correction is being re-picked.
    setLesseeDialogOpen(true);
  };

  const setRejectBucket = () => {
    // Phase 1 Reject — always open the reason/comment picker (both fields
    // required by the backend). Pre-seeds from the existing rejected
    // allocation when the user is re-picking (rare — usually they'd Clear).
    setRejectDialogOpen(true);
  };

  const clear = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/equipment-repairs/corrections/${correction.id}/unallocate`);
      onChange?.(data);
      toast.success("Allocation cleared");
      window.dispatchEvent(new Event("repairs:refresh-badge"));
    } catch (e) { toast.error(extractError(e)); }
    finally { setBusy(false); }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/equipment-repairs/corrections/${correction.id}/commit`);
      onChange?.({ id: correction.id, status: data.correction_id ? undefined : undefined, ...data });
      const bits = [];
      if (data.expenses_created) bits.push(`${data.expenses_created} expense${data.expenses_created > 1 ? "s" : ""}`);
      if (data.alvys_synced) bits.push(`${data.alvys_synced} Alvys sync`);
      if (data.alvys_failed) bits.push(`${data.alvys_failed} sync failed`);
      toast.success("Committed" + (bits.length ? " · " + bits.join(" · ") : ""));
      window.dispatchEvent(new Event("repairs:refresh-badge"));
    } catch (e) { toast.error(extractError(e)); }
    finally { setBusy(false); }
  };

  if (readOnly) {
    if (!correction.charge_allocations || correction.charge_allocations.length === 0) {
      return <Badge variant="secondary" className="bg-amber-50 text-amber-700 hover:bg-amber-50">Unassigned</Badge>;
    }
    return (
      <div className="space-y-0.5 text-xs text-slate-600">
        {correction.charge_allocations.map((a, i) => (
          <div key={i}>
            <span className="font-mono">{a.charge_type}</span>: {formatMoney(a.amount)}
            {a.charge_type === "driver" && a.driver_name && (
              <span className="text-slate-500"> → {a.driver_name}</span>
            )}
            {a.charge_type === "lessee" && a.lessee_name && (
              <span className="text-slate-500"> → {a.lessee_name}</span>
            )}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-1" data-testid={`alloc-cell-${correction.id}`}>
      {/* Chips row */}
      <div className="flex flex-wrap items-center gap-1">
        {buckets.map((b) => {
          const meta = CHIP_META[b];
          const active = currentBucket === b;
          const onClick = () => {
            if (b === "driver") return setDriverBucket();
            if (b === "lessee") return setLesseeBucket();
            if (b === "rejected") return setRejectBucket();
            return setSingle(b);
          };
          return (
            <button
              key={b}
              disabled={busy || isCharged}
              onClick={onClick}
              className={`rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors disabled:opacity-40 ${
                active ? "ring-1 ring-inset ring-slate-900 " + meta.classes : meta.classes
              }`}
              data-testid={`alloc-chip-${b}-${correction.id}`}
            >
              {meta.label}
            </button>
          );
        })}
        <SplitPopover
          correction={correction}
          invoice={invoice}
          open={splitOpen}
          onOpenChange={setSplitOpen}
          onApply={applyAllocations}
          disabled={busy || isCharged}
          isLeasedOut={isLeasedOut}
        />
        {(currentBucket && !isCharged) && (
          <button
            disabled={busy}
            onClick={clear}
            className="ml-1 rounded-full border border-slate-200 bg-white px-2 py-0.5 text-xs text-slate-500 hover:bg-slate-50"
            data-testid={`alloc-clear-${correction.id}`}
          >Clear</button>
        )}
      </div>

      {/* Sub-line: current allocation summary + commit pill */}
      {currentBucket && (
        <div className="flex items-center gap-2 text-xs">
          {currentBucket === "split" ? (
            <span className="text-slate-500">
              {correction.charge_allocations.map((a, i) => (
                <span key={i} className="mr-1"><span className="font-mono text-slate-600">{a.charge_type}</span> {formatMoney(a.amount)}{i < correction.charge_allocations.length - 1 ? " ·" : ""}</span>
              ))}
            </span>
          ) : (
            <>
              {currentBucket === "driver" && correction.charge_allocations[0]?.driver_name && (
                <span className="text-slate-500">→ {correction.charge_allocations[0].driver_name}</span>
              )}
              {currentBucket === "lessee" && correction.charge_allocations[0]?.lessee_name && (
                <span className="text-slate-500" data-testid={`alloc-lessee-summary-${correction.id}`}>
                  → {correction.charge_allocations[0].lessee_name}
                  {correction.charge_allocations[0]?.lessee_contact && (
                    <span className="ml-1 text-slate-400">· {correction.charge_allocations[0].lessee_contact}</span>
                  )}
                </span>
              )}
            </>
          )}
          {!isCharged && (
            <button
              disabled={busy}
              onClick={commit}
              className="ml-auto rounded-full bg-emerald-600 px-2 py-0.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-60"
              data-testid={`commit-row-${correction.id}`}
            >
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : "Commit"}
            </button>
          )}
          {isCharged && (
            <div className="ml-auto flex items-center gap-2">
              <Badge className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50">
                <Check className="mr-1 h-3 w-3" /> Charged
              </Badge>
              {canReverse && (
                <button
                  type="button"
                  onClick={() => { setReverseError(null); setReverseOpen(true); }}
                  className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-0.5 text-[11px] text-slate-600 hover:bg-slate-50 hover:text-rose-700"
                  data-testid={`reverse-charge-${correction.id}`}
                  title="Reverse these charges to re-allocate"
                >
                  <Edit3 className="h-3 w-3" /> Edit
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Reverse-charge confirmation dialog — itemized preview + retry-safe
          error banner. Modal stays open on 424 alvys_void_failed. */}
      {canReverse && (
        <Dialog open={reverseOpen} onOpenChange={(v) => { if (!reversing) setReverseOpen(v); }}>
          <DialogContent data-testid={`reverse-charge-dialog-${correction.id}`}>
            <DialogHeader>
              <DialogTitle>Reverse charges for this correction?</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              <div>This will:</div>
              <ul className="space-y-1.5 rounded-md border border-slate-200 bg-slate-50 p-2 text-slate-700">
                {(correction.charge_allocations || []).map((a, i) => {
                  const bucket = a.charge_type;
                  const shortId = a.expense_id ? `#${String(a.expense_id).slice(-6)}` : null;
                  const shortDed = a.alvys_deduction_id ? String(a.alvys_deduction_id).slice(0, 8) + "…" : null;
                  return (
                    <li key={i} className="flex items-start gap-2 leading-tight">
                      <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-slate-400" />
                      <span>
                        {bucket === "warranty" ? (
                          <>Clear <span className="font-medium">warranty</span> allocation · {formatMoney(a.amount)}</>
                        ) : bucket === "duplicate" ? (
                          <>Clear <span className="font-medium">duplicate</span> allocation · {formatMoney(a.amount)}</>
                        ) : bucket === "company" ? (
                          <>Void expense {shortId ? <span className="font-mono text-xs">{shortId}</span> : null} · {formatMoney(a.amount)} (<span className="font-medium">Company</span>)</>
                        ) : bucket === "lessee" ? (
                          <>
                            Void expense {shortId ? <span className="font-mono text-xs">{shortId}</span> : null} · {formatMoney(a.amount)}
                            {" ("}<span className="font-medium">Lessee</span>
                            {a.lessee_name ? <> — {a.lessee_name}</> : null}
                            {")"}
                          </>
                        ) : (
                          <>
                            Void expense {shortId ? <span className="font-mono text-xs">{shortId}</span> : null} · {formatMoney(a.amount)}
                            {" ("}<span className="font-medium">Driver</span>
                            {a.driver_name ? <> — {a.driver_name}</> : null}
                            {")"}
                            {shortDed && (
                              <div className="ml-4 text-xs text-slate-500">+ Alvys deduction {shortDed}</div>
                            )}
                          </>
                        )}
                      </span>
                    </li>
                  );
                })}
              </ul>
              <div className="text-xs text-slate-500">
                The correction will return to <strong>Unassigned</strong>. Committed expenses will be marked{" "}
                <span className="rounded bg-slate-200 px-1 py-0.5 font-mono text-[11px]">voided</span> (kept for audit). Action is logged.
              </div>
              {reverseError && (
                <Alert variant="destructive" data-testid={`reverse-charge-error-${correction.id}`}>
                  <AlertDescription className="text-xs">{reverseError}</AlertDescription>
                </Alert>
              )}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setReverseOpen(false)}
                disabled={reversing}
                data-testid={`reverse-charge-cancel-${correction.id}`}
              >
                Cancel
              </Button>
              <Button
                onClick={reverseCharge}
                disabled={reversing}
                className="bg-rose-600 text-white hover:bg-rose-700"
                data-testid={`reverse-charge-confirm-${correction.id}`}
              >
                {reversing ? (<><Loader2 className="mr-1 h-3 w-3 animate-spin" /> Reversing…</>) : "Reverse charges"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Driver picker modal — always shown when the user clicks the
          "Driver" chip. Pre-seeded to `invoice.suggested_*` when a
          suggestion exists. If the user commits an id different from
          the suggestion, `overrideFrom` propagates the original suggested
          id/name into the allocation for the audit trail. */}
      {driverDialogOpen && (
        <DriverPickerOverlay
          suggestedId={invoice.suggested_driver_id || null}
          suggestedName={invoice.suggested_driver_name || null}
          onCancel={() => setDriverDialogOpen(false)}
          onPick={(id, nm) => {
            setDriverDialogOpen(false);
            const overrideFrom = invoice.suggested_driver_id
              ? { id: invoice.suggested_driver_id, name: invoice.suggested_driver_name }
              : null;
            setSingle("driver", id, nm, overrideFrom);
          }}
        />
      )}

      {/* Lessee picker modal — Phase 2a. Shown when the user clicks the
          "Lessee" chip on a leased-out invoice. Free-text with historical
          autocomplete against /equipment-repairs/lessees/search. */}
      {lesseeDialogOpen && (
        <LesseePickerOverlay
          companyId={invoice?.company_id}
          existing={correction.charge_allocations?.[0]?.charge_type === "lessee"
            ? correction.charge_allocations[0]
            : null}
          onCancel={() => setLesseeDialogOpen(false)}
          onPick={(payload) => {
            setLesseeDialogOpen(false);
            setLesseeSingle(payload);
          }}
        />
      )}

      {/* Reject picker modal — Phase 1 Reject. Line-level rejected
          allocation requires reason + comment (backend allocations
          validator enforces both). Pre-seeds from existing rejected
          allocation on the line, when present. */}
      {rejectDialogOpen && (
        <RejectPickerOverlay
          existing={(correction.charge_allocations || []).find(
            (a) => a.charge_type === "rejected")}
          onCancel={() => setRejectDialogOpen(false)}
          onPick={(payload) => {
            setRejectDialogOpen(false);
            setRejectSingle(payload);
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// LesseePickerOverlay — Phase 2a. Free-text lessee name (required) +
// optional contact. Historical autocomplete from prior lessee allocations.
// ---------------------------------------------------------------------------
function LesseePickerOverlay({ companyId, existing, onCancel, onPick }) {
  const [lesseeName, setLesseeName] = useState(existing?.lessee_name || "");
  const [lesseeContact, setLesseeContact] = useState(existing?.lessee_contact || "");
  const [source, setSource] = useState(existing?.lessee_source || "manual");
  const canSubmit = lesseeName.trim().length > 0;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4" onClick={onCancel}>
      <div
        className="w-full max-w-md rounded-md bg-white p-4 shadow"
        onClick={(e) => e.stopPropagation()}
        data-testid="lessee-picker-overlay"
      >
        <div className="mb-1 text-sm font-medium">Assign lessee</div>
        <p className="mb-3 text-xs text-slate-500">
          This unit is leased out. Charge is tracked internally against the lessee below.
        </p>
        <div className="space-y-3">
          <div>
            <Label className="mb-1 block text-xs">Lessee name</Label>
            <LesseeCombobox
              companyId={companyId}
              value={lesseeName}
              onChange={(name, src) => { setLesseeName(name); setSource(src || "manual"); }}
              autoFocus
              testid="alloc-lessee-picker"
            />
          </div>
          <div>
            <Label className="mb-1 block text-xs">Contact (optional)</Label>
            <Input
              value={lesseeContact}
              onChange={(e) => setLesseeContact(e.target.value)}
              placeholder="Phone, email, or other contact info (optional)"
              className="h-9 text-sm"
              data-testid="alloc-lessee-contact"
            />
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onCancel}>Cancel</Button>
          <Button
            size="sm"
            disabled={!canSubmit}
            onClick={() => canSubmit && onPick({
              lessee_name: lesseeName.trim(),
              lessee_contact: lesseeContact.trim() || null,
              lessee_source: source,
            })}
            className="bg-[#0F1E3D] text-white hover:bg-[#0F1E3D]/90"
            data-testid="alloc-lessee-confirm"
          >
            Assign
          </Button>
        </div>
      </div>
    </div>
  );
}

function DriverPickerOverlay({ suggestedId = null, suggestedName = null, onCancel, onPick }) {
  const [driverId, setDriverId] = useState(suggestedId);
  const [driverName, setDriverName] = useState(suggestedName || "");
  const isOverride = suggestedId && driverId && driverId !== suggestedId;
  const noSuggestion = !suggestedId;
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4" onClick={onCancel}>
      <div className="w-full max-w-md rounded-md bg-white p-4 shadow" onClick={(e) => e.stopPropagation()}>
        <div className="mb-2 text-sm font-medium">
          {noSuggestion ? "Select driver" : "Confirm driver"}
        </div>
        <p className="mb-3 text-xs text-slate-500">
          {noSuggestion
            ? "No suggestion from Alvys — pick manually."
            : "Suggested driver is pre-selected. Change it to override."}
        </p>
        <DriverCombobox
          value={driverId} name={driverName}
          onChange={(id, nm) => { setDriverId(id); setDriverName(nm || ""); }}
          autoFocus
          testid="alloc-driver-picker"
        />
        {isOverride && (
          <div
            className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-800"
            data-testid="driver-override-note"
          >
            Overriding suggested driver:{" "}
            <span className="font-medium">{suggestedName || "—"}</span>
            {" → "}
            <span className="font-medium">{driverName || "…"}</span>
          </div>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onCancel}>Cancel</Button>
          <Button
            size="sm"
            disabled={!driverId}
            onClick={() => driverId && onPick(driverId, driverName)}
            className="bg-[#0F1E3D] text-white hover:bg-[#0F1E3D]/90"
            data-testid="alloc-driver-confirm"
          >
            {isOverride ? "Override & assign" : "Assign"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// RejectPickerOverlay — Phase 1 Reject. Line-level rejected bucket needs
// reason + comment (both required by the backend allocations validator).
// Same small-modal pattern as Lessee/Driver pickers. Rose accents match
// the invoice-level REJECTED pill.
// ---------------------------------------------------------------------------
const REJECT_REASONS = [
  { value: "wrong_company",  label: "Wrong Company" },
  { value: "equipment_sold", label: "Equipment Sold" },
  { value: "other",          label: "Other" },
];

function RejectPickerOverlay({ existing, onCancel, onPick }) {
  const [reason, setReason] = useState(existing?.reject_reason || "");
  const [comment, setComment] = useState(existing?.reject_comment || "");
  const trimmed = comment.trim();
  const canSubmit = !!reason && trimmed.length > 0;
  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-md rounded-md bg-white p-4 shadow"
        onClick={(e) => e.stopPropagation()}
        data-testid="alloc-reject-picker-overlay"
      >
        <div className="mb-1 text-sm font-medium text-rose-700">Reject this line</div>
        <p className="mb-3 text-xs text-slate-500">
          Marks this correction as rejected and rolls it into a Shop Credit claim to the shop.
          Both fields required.
        </p>
        <div className="space-y-3">
          <div>
            <Label className="mb-1 block text-xs">Reason</Label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm"
              data-testid="alloc-reject-reason"
            >
              <option value="">Pick a reason…</option>
              {REJECT_REASONS.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="mb-1 block text-xs">
              Comment <span className="text-rose-600">*</span>
            </Label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              placeholder="Why is this line being rejected?"
              className="w-full rounded-md border border-slate-200 bg-white p-2 text-sm"
              data-testid="alloc-reject-comment"
            />
            {comment.length > 0 && trimmed.length === 0 && (
              <div className="mt-1 text-[11px] text-rose-600">Comment cannot be blank.</div>
            )}
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onCancel} data-testid="alloc-reject-cancel">
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={!canSubmit}
            onClick={() => canSubmit && onPick({ reject_reason: reason, reject_comment: trimmed })}
            className="bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-60"
            data-testid="alloc-reject-confirm"
          >
            Reject line
          </Button>
        </div>
      </div>
    </div>
  );
}



// ---------------------------------------------------------------------------
// SplitPopover — multi-bucket amounts (must sum to correction.amount)
// ---------------------------------------------------------------------------
function SplitPopover({ correction, invoice, open, onOpenChange, onApply, disabled, isLeasedOut = false }) {
  const [warranty, setWarranty] = useState("");
  const [company, setCompany] = useState("");
  const [driver, setDriver] = useState("");
  const [driverId, setDriverId] = useState(invoice.suggested_driver_id || null);
  const [driverName, setDriverName] = useState(invoice.suggested_driver_name || "");
  // Phase 2a — parallel state for the Lessee split row (replaces Driver row
  // when the invoice is leased-out). Kept separate from driver state so
  // switching invoices doesn't cross-contaminate.
  const [lessee, setLessee] = useState("");
  const [lesseeName, setLesseeName] = useState("");
  const [lesseeContact, setLesseeContact] = useState("");
  const [lesseeSource, setLesseeSource] = useState("manual");

  useEffect(() => {
    if (open) {
      // Seed from existing allocations
      setWarranty(""); setCompany(""); setDriver(""); setLessee("");
      setLesseeName(""); setLesseeContact(""); setLesseeSource("manual");
      (correction.charge_allocations || []).forEach((a) => {
        if (a.charge_type === "warranty") setWarranty(String(a.amount));
        else if (a.charge_type === "company") setCompany(String(a.amount));
        else if (a.charge_type === "driver") {
          setDriver(String(a.amount));
          setDriverId(a.assigned_driver_id || invoice.suggested_driver_id);
          setDriverName(a.driver_name || invoice.suggested_driver_name || "");
        }
        else if (a.charge_type === "lessee") {
          setLessee(String(a.amount));
          setLesseeName(a.lessee_name || "");
          setLesseeContact(a.lessee_contact || "");
          setLesseeSource(a.lessee_source || "manual");
        }
      });
    }
  }, [open, correction.charge_allocations, invoice.suggested_driver_id, invoice.suggested_driver_name]);

  const thirdBucket = isLeasedOut ? "lessee" : "driver";
  const parts = [
    { bucket: "warranty", v: warranty },
    { bucket: "company", v: company },
    { bucket: thirdBucket, v: isLeasedOut ? lessee : driver },
  ].filter((p) => Number(p.v) > 0);
  const sum = parts.reduce((s, p) => s + (Number(p.v) || 0), 0);
  const target = Number(correction.amount);
  const summed = closeTo(sum, target);
  const driverPresent = !isLeasedOut && Number(driver) > 0;
  const lesseePresent = isLeasedOut && Number(lessee) > 0;

  const submit = async () => {
    const allocs = [];
    if (Number(warranty) > 0) allocs.push({ charge_type: "warranty", amount: Number(warranty) });
    if (Number(company) > 0) allocs.push({ charge_type: "company", amount: Number(company) });
    if (!isLeasedOut && Number(driver) > 0) {
      if (!driverId) { toast.error("Pick a driver for the driver share"); return; }
      const entry = { charge_type: "driver", amount: Number(driver), assigned_driver_id: driverId, driver_name: driverName };
      // Stamp override-audit fields when the user picked a driver different
      // from the invoice suggestion in the split flow too.
      if (invoice.suggested_driver_id && driverId !== invoice.suggested_driver_id) {
        entry.driver_overridden_from_suggested_id = invoice.suggested_driver_id;
        entry.driver_overridden_from_suggested_name = invoice.suggested_driver_name || "";
      }
      allocs.push(entry);
    }
    if (isLeasedOut && Number(lessee) > 0) {
      if (!lesseeName.trim()) { toast.error("Enter a lessee name for the lessee share"); return; }
      allocs.push({
        charge_type: "lessee",
        amount: Number(lessee),
        lessee_name: lesseeName.trim(),
        lessee_contact: lesseeContact.trim() || null,
        lessee_source: lesseeSource || "manual",
      });
    }
    if (allocs.length === 0) { toast.error("Add at least one amount"); return; }
    onOpenChange(false);
    await onApply(allocs);
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          disabled={disabled}
          className="rounded-full border border-slate-200 bg-white px-2.5 py-0.5 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-40"
          data-testid={`alloc-split-${correction.id}`}
        >
          <Split className="mr-1 inline h-3 w-3" /> Split…
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-96 space-y-3" align="end">
        <div className="text-xs text-slate-500">
          Total to allocate: <span className="font-medium text-slate-900">{formatMoney(target)}</span>
        </div>
        <div className="space-y-2">
          <SplitRow label="Warranty" value={warranty} onChange={setWarranty} />
          <SplitRow label="Company" value={company} onChange={setCompany} />
          {isLeasedOut ? (
            <SplitRow label="Lessee" value={lessee} onChange={setLessee} />
          ) : (
            <SplitRow label="Driver" value={driver} onChange={setDriver} />
          )}
          {driverPresent && (
            <div className="pl-6 space-y-1.5">
              <DriverCombobox
                value={driverId} name={driverName}
                onChange={(id, nm) => { setDriverId(id); setDriverName(nm || ""); }}
                testid={`alloc-split-driver-${correction.id}`}
              />
              {invoice.suggested_driver_id && driverId && driverId !== invoice.suggested_driver_id && (
                <div
                  className="rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] text-amber-800"
                  data-testid={`alloc-split-override-note-${correction.id}`}
                >
                  Overriding suggested driver:{" "}
                  <span className="font-medium">{invoice.suggested_driver_name || "—"}</span>
                  {" → "}
                  <span className="font-medium">{driverName || "…"}</span>
                </div>
              )}
            </div>
          )}
          {lesseePresent && (
            <div className="pl-6 space-y-1.5">
              <LesseeCombobox
                companyId={invoice?.company_id}
                value={lesseeName}
                onChange={(nm, src) => { setLesseeName(nm); setLesseeSource(src || "manual"); }}
                testid={`alloc-split-lessee-${correction.id}`}
              />
              <Input
                value={lesseeContact}
                onChange={(e) => setLesseeContact(e.target.value)}
                placeholder="Phone, email, or other contact info (optional)"
                className="h-8 text-xs"
                data-testid={`alloc-split-lessee-contact-${correction.id}`}
              />
            </div>
          )}
        </div>
        <div className={`rounded-md px-3 py-2 text-xs ${summed ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>
          Sum: {formatMoney(sum)} / {formatMoney(target)}
          {!summed && ` — off by ${formatMoney(Math.abs(sum - target))}`}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button size="sm" disabled={!summed} onClick={submit} className="bg-[#0F1E3D] text-white hover:bg-[#0F1E3D]/90"
                  data-testid={`alloc-split-apply-${correction.id}`}>
            Apply split
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function SplitRow({ label, value, onChange }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <Label className="w-20 shrink-0 text-xs">{label}</Label>
      <Input type="number" min="0" step="0.01" value={value}
             onChange={(e) => onChange(e.target.value)}
             className="h-8" placeholder="0.00" />
    </div>
  );
}
